import { captureMessage } from "@sentry/gatsby";
import type { ZodError } from "zod";
import { pathToSegments } from "./paths";

const reportedInvalidTypes = new Set<string>();

// users/abc123/bets/xyz -> users/{id}/bets/{id}
const normalizePath = (path: string) =>
  pathToSegments(path)
    .map((segment, index) => (index % 2 === 1 ? "{id}" : segment))
    .join("/");

const issueSignature = (errors: ZodError, isCollection: boolean) =>
  [
    ...new Set(
      errors.issues.map(
        (issue) =>
          // drop the array index so items with the same problem dedupe together
          `${issue.path.slice(isCollection ? 1 : 0).join(".")}:${issue.code}`,
      ),
    ),
  ]
    .sort()
    .join("|");

export const captureInvalidType = (
  data: any,
  errors: ZodError,
  path: string,
) => {
  const isCollection = Array.isArray(data);
  const normalizedPath = normalizePath(path);
  const signature = issueSignature(errors, isCollection);

  // a schema mismatch doesn't change between snapshots, so report each
  // distinct failure once per session instead of on every update
  const dedupeKey = `${normalizedPath}::${signature}`;
  if (reportedInvalidTypes.has(dedupeKey)) {
    return;
  }
  reportedInvalidTypes.add(dedupeKey);

  let extraData = { data, errors, path };

  // if data is an array, isolate only the problematic items to avoid truncation
  if (isCollection) {
    const invalidIndices = new Set(
      errors.issues
        .map((issue) => issue.path[0]) // the index of the item that failed validation
        .filter(
          (index): index is number =>
            typeof index === "number" && index < data.length,
        ),
    );

    if (invalidIndices.size > 0) {
      const invalidData = Array.from(invalidIndices).map((index) => ({
        index,
        item: data[index],
      }));

      extraData = {
        data: invalidData,
        errors,
        path,
      };
    }
  }

  captureMessage(
    `Invalid data type received from firestore. Path ${normalizedPath}`,
    {
      level: "warning",
      fingerprint: ["firestore-invalid-type", normalizedPath, signature],
      tags: { collection: pathToSegments(path)[0] },
      extra: extraData,
    },
  );
};
